/**
 * EXPECTATIONS PAGE - Track what you're waiting for from others
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PlusIcon, CheckIcon, XMarkIcon, EnvelopeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { Screen, Section, Button, Tabs, Panel, Badge, Spinner, Alert } from '../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import ExpectationForm from '../components/forms/ExpectationForm';
import { format, parseISO, isPast, isThisWeek } from 'date-fns';

interface Person {
  id: number;
  name: string;
}

interface Expectation {
  id: number;
  description: string;
  from_text: string;
  persons: { id: number; name: string }[];
  email: number | null;
  email_detail: { id: number; subject: string; sender: string } | null;
  project: number | null;
  project_detail: { id: number; name: string } | null;
  business: number | null;
  business_detail: { id: number; name: string } | null;
  expected_by: string | null;
  status: 'pending' | 'fulfilled' | 'expired';
  created_at: string;
  fulfilled_at: string | null;
  notes: string;
}

type TabType = 'all' | 'pending' | 'fulfilled' | 'expired';

const ExpectationsPage: React.FC = () => {
  const [expectations, setExpectations] = useState<Expectation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [groupByPerson, setGroupByPerson] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExpectation, setEditingExpectation] = useState<Expectation | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: number]: boolean }>({});

  const fetchExpectations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/expectations/');
      setExpectations(response.data || []);
    } catch (err) {
      console.error('Error fetching expectations:', err);
      setError('Failed to load expectations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpectations();
  }, [fetchExpectations]);

  const filteredExpectations = useMemo(() => {
    if (activeTab === 'all') return expectations;
    return expectations.filter(e => e.status === activeTab);
  }, [expectations, activeTab]);

  const groupedExpectations = useMemo(() => {
    if (!groupByPerson) return null;
    const groups: { [personId: number]: { person: { id: number; name: string }; expectations: Expectation[] } } = {};
    filteredExpectations.forEach(exp => {
      // Group by each person (an expectation can appear in multiple groups)
      if (exp.persons && exp.persons.length > 0) {
        exp.persons.forEach(person => {
          if (!groups[person.id]) {
            groups[person.id] = {
              person: person,
              expectations: []
            };
          }
          groups[person.id].expectations.push(exp);
        });
      } else {
        // No persons - put in "Unassigned" group
        if (!groups[0]) {
          groups[0] = { person: { id: 0, name: 'Unassigned' }, expectations: [] };
        }
        groups[0].expectations.push(exp);
      }
    });
    return Object.values(groups).sort((a, b) => a.person.name.localeCompare(b.person.name));
  }, [filteredExpectations, groupByPerson]);

  // Summary stats
  const stats = useMemo(() => {
    const pending = expectations.filter(e => e.status === 'pending');
    const overdue = pending.filter(e => e.expected_by && isPast(parseISO(e.expected_by)));
    const dueThisWeek = pending.filter(e => e.expected_by && isThisWeek(parseISO(e.expected_by)));
    return {
      totalPending: pending.length,
      overdue: overdue.length,
      dueThisWeek: dueThisWeek.length,
    };
  }, [expectations]);

  const handleMarkFulfilled = async (id: number) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      const response = await apiClient.post(`/api/expectations/${id}/mark_fulfilled/`);
      setExpectations(prev => prev.map(e => e.id === id ? response.data : e));
    } catch (err) {
      console.error('Error marking fulfilled:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleMarkExpired = async (id: number) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      const response = await apiClient.post(`/api/expectations/${id}/mark_expired/`);
      setExpectations(prev => prev.map(e => e.id === id ? response.data : e));
    } catch (err) {
      console.error('Error marking expired:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this expectation?')) return;
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await apiClient.delete(`/api/expectations/${id}/`);
      setExpectations(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error('Error deleting:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleEdit = (expectation: Expectation) => {
    setEditingExpectation(expectation);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingExpectation(null);
  };

  const handleExpectationCreated = () => {
    fetchExpectations();
    handleFormClose();
  };

  const handleExpectationUpdated = () => {
    fetchExpectations();
    handleFormClose();
  };

  const isOverdue = (expectedBy: string | null) => {
    if (!expectedBy) return false;
    return isPast(parseISO(expectedBy));
  };

  const getStatusBadge = (status: string, expectedBy: string | null) => {
    if (status === 'fulfilled') {
      return <Badge variant="success">Fulfilled</Badge>;
    }
    if (status === 'expired') {
      return <Badge variant="default">Expired</Badge>;
    }
    if (isOverdue(expectedBy)) {
      return <Badge variant="error">Overdue</Badge>;
    }
    return <Badge variant="warning">Pending</Badge>;
  };

  const renderExpectationRow = (expectation: Expectation) => (
    <tr key={expectation.id} style={{ borderBottom: `1px solid ${COLORS.border.default}` }}>
      <td style={{ padding: `${SPACING[12]} ${SPACING[16]}`, fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.primary }}>
        {expectation.description}
      </td>
      <td style={{ padding: `${SPACING[12]} ${SPACING[16]}`, fontSize: TYPOGRAPHY.size.sm }}>
        {expectation.from_text && (
          <span style={{ color: COLORS.text.primary }}>{expectation.from_text}</span>
        )}
        {expectation.from_text && expectation.persons && expectation.persons.length > 0 && (
          <span style={{ color: COLORS.text.tertiary }}> + </span>
        )}
        {expectation.persons && expectation.persons.length > 0 && (
          <span style={{ display: 'inline' }}>
            {expectation.persons.map((person, idx) => (
              <span key={person.id}>
                <Link
                  to={`/people/${person.id}`}
                  style={{ color: COLORS.accent.default, textDecoration: 'none' }}
                >
                  {person.name}
                </Link>
                {idx < expectation.persons.length - 1 && ', '}
              </span>
            ))}
          </span>
        )}
        {!expectation.from_text && (!expectation.persons || expectation.persons.length === 0) && (
          <span style={{ color: COLORS.text.tertiary }}>-</span>
        )}
      </td>
      <td style={{
        padding: `${SPACING[12]} ${SPACING[16]}`,
        fontSize: TYPOGRAPHY.size.sm,
        color: expectation.expected_by && isOverdue(expectation.expected_by) && expectation.status === 'pending'
          ? '#DC2626'
          : COLORS.text.secondary
      }}>
        {expectation.expected_by ? format(parseISO(expectation.expected_by), 'MMM d, yyyy') : '-'}
      </td>
      <td style={{ padding: `${SPACING[12]} ${SPACING[16]}` }}>
        {getStatusBadge(expectation.status, expectation.expected_by)}
      </td>
      <td style={{ padding: `${SPACING[12]} ${SPACING[16]}`, fontSize: TYPOGRAPHY.size.sm }}>
        {expectation.email_detail ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: COLORS.text.secondary,
              fontSize: TYPOGRAPHY.body.sizes.tiny,
            }}
            title={expectation.email_detail.subject}
          >
            <EnvelopeIcon style={{ width: '14px', height: '14px', marginRight: SPACING[4] }} />
            {expectation.email_detail.subject.substring(0, 30)}
            {expectation.email_detail.subject.length > 30 ? '...' : ''}
          </span>
        ) : (
          <span style={{ color: COLORS.text.tertiary }}>-</span>
        )}
      </td>
      <td style={{ padding: `${SPACING[12]} ${SPACING[16]}` }}>
        <div style={{ display: 'flex', gap: SPACING[8] }}>
          {expectation.status === 'pending' && (
            <>
              <button
                onClick={() => handleMarkFulfilled(expectation.id)}
                disabled={actionLoading[expectation.id]}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: `${SPACING[4]} ${SPACING[8]}`,
                  border: 'none',
                  borderRadius: RADIUS,
                  fontSize: TYPOGRAPHY.body.sizes.tiny,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  backgroundColor: '#DCFCE7',
                  color: '#166534',
                  cursor: actionLoading[expectation.id] ? 'not-allowed' : 'pointer',
                  opacity: actionLoading[expectation.id] ? 0.5 : 1,
                }}
                title="Mark as fulfilled"
              >
                <CheckIcon style={{ width: '12px', height: '12px', marginRight: SPACING[4] }} />
                Done
              </button>
              <button
                onClick={() => handleMarkExpired(expectation.id)}
                disabled={actionLoading[expectation.id]}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: `${SPACING[4]} ${SPACING[8]}`,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  fontSize: TYPOGRAPHY.body.sizes.tiny,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  backgroundColor: COLORS.bg.panel,
                  color: COLORS.text.secondary,
                  cursor: actionLoading[expectation.id] ? 'not-allowed' : 'pointer',
                  opacity: actionLoading[expectation.id] ? 0.5 : 1,
                }}
                title="Mark as expired/cancelled"
              >
                <XMarkIcon style={{ width: '12px', height: '12px', marginRight: SPACING[4] }} />
                Cancel
              </button>
            </>
          )}
          <button
            onClick={() => handleEdit(expectation)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: `${SPACING[4]} ${SPACING[8]}`,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.body.sizes.tiny,
              backgroundColor: COLORS.bg.panel,
              color: COLORS.text.secondary,
              cursor: 'pointer',
            }}
            title="Edit"
          >
            <PencilIcon style={{ width: '12px', height: '12px' }} />
          </button>
          <button
            onClick={() => handleDelete(expectation.id)}
            disabled={actionLoading[expectation.id]}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: `${SPACING[4]} ${SPACING[8]}`,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.body.sizes.tiny,
              backgroundColor: COLORS.bg.panel,
              color: '#DC2626',
              cursor: actionLoading[expectation.id] ? 'not-allowed' : 'pointer',
              opacity: actionLoading[expectation.id] ? 0.5 : 1,
            }}
            title="Delete"
          >
            <TrashIcon style={{ width: '12px', height: '12px' }} />
          </button>
        </div>
      </td>
    </tr>
  );

  const renderTable = (data: Expectation[]) => (
    <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS }}>
      <table style={{ minWidth: '100%', backgroundColor: COLORS.bg.panel, borderCollapse: 'collapse' }}>
        <thead style={{ backgroundColor: COLORS.bg.base }}>
          <tr>
            <th style={{ padding: `${SPACING[12]} ${SPACING[16]}`, textAlign: 'left', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>
              Description
            </th>
            <th style={{ padding: `${SPACING[12]} ${SPACING[16]}`, textAlign: 'left', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>
              From
            </th>
            <th style={{ padding: `${SPACING[12]} ${SPACING[16]}`, textAlign: 'left', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>
              Expected By
            </th>
            <th style={{ padding: `${SPACING[12]} ${SPACING[16]}`, textAlign: 'left', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>
              Status
            </th>
            <th style={{ padding: `${SPACING[12]} ${SPACING[16]}`, textAlign: 'left', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>
              Source Email
            </th>
            <th style={{ padding: `${SPACING[12]} ${SPACING[16]}`, textAlign: 'left', fontSize: TYPOGRAPHY.body.sizes.small, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map(renderExpectationRow)}
        </tbody>
      </table>
    </div>
  );

  return (
    <Screen>
      <Section
        title="Expectations"
        action={
          <Button
            variant="primary"
            onClick={() => setIsFormOpen(true)}
            icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}
          >
            New Expectation
          </Button>
        }
      >
        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: SPACING[16], marginBottom: SPACING[24] }}>
          <Panel style={{ padding: SPACING[16], textAlign: 'center' }}>
            <div style={{ fontSize: TYPOGRAPHY.size['2xl'], fontWeight: TYPOGRAPHY.weight.bold, color: COLORS.text.primary }}>
              {stats.totalPending}
            </div>
            <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary }}>Pending</div>
          </Panel>
          <Panel style={{ padding: SPACING[16], textAlign: 'center', borderColor: stats.overdue > 0 ? '#FCA5A5' : undefined }}>
            <div style={{ fontSize: TYPOGRAPHY.size['2xl'], fontWeight: TYPOGRAPHY.weight.bold, color: stats.overdue > 0 ? '#DC2626' : COLORS.text.primary }}>
              {stats.overdue}
            </div>
            <div style={{ fontSize: TYPOGRAPHY.size.sm, color: stats.overdue > 0 ? '#DC2626' : COLORS.text.secondary }}>Overdue</div>
          </Panel>
          <Panel style={{ padding: SPACING[16], textAlign: 'center' }}>
            <div style={{ fontSize: TYPOGRAPHY.size['2xl'], fontWeight: TYPOGRAPHY.weight.bold, color: COLORS.text.primary }}>
              {stats.dueThisWeek}
            </div>
            <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary }}>Due This Week</div>
          </Panel>
        </div>

        {/* Tabs and Group Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
          <Tabs
            tabs={[
              { id: 'pending', label: 'Pending' },
              { id: 'fulfilled', label: 'Fulfilled' },
              { id: 'expired', label: 'Expired' },
              { id: 'all', label: 'All' },
            ]}
            activeTab={activeTab}
            onChange={(tab) => setActiveTab(tab as TabType)}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={groupByPerson}
              onChange={(e) => setGroupByPerson(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            Group by Person
          </label>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[48] }}>
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <Alert variant="error">{error}</Alert>
        ) : filteredExpectations.length === 0 ? (
          <Panel style={{ padding: SPACING[48], textAlign: 'center' }}>
            <p style={{ fontSize: TYPOGRAPHY.size.lg, color: COLORS.text.secondary, marginBottom: SPACING[16] }}>
              No expectations found
            </p>
            <Button variant="primary" onClick={() => setIsFormOpen(true)}>
              Create your first expectation
            </Button>
          </Panel>
        ) : groupByPerson && groupedExpectations ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            {groupedExpectations.map(group => (
              <div key={group.person.id}>
                <h3 style={{ fontSize: TYPOGRAPHY.size.lg, fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary, marginBottom: SPACING[12] }}>
                  <Link to={`/people/${group.person.id}`} style={{ color: COLORS.accent.default, textDecoration: 'none' }}>
                    {group.person.name}
                  </Link>
                  <span style={{ marginLeft: SPACING[8], fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>
                    ({group.expectations.length})
                  </span>
                </h3>
                {renderTable(group.expectations)}
              </div>
            ))}
          </div>
        ) : (
          renderTable(filteredExpectations)
        )}
      </Section>

      {/* Form Modal */}
      <ExpectationForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onExpectationCreated={handleExpectationCreated}
        onExpectationUpdated={handleExpectationUpdated}
        initialExpectationData={editingExpectation}
        isEditMode={!!editingExpectation}
      />
    </Screen>
  );
};

export default ExpectationsPage;
